import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, Users, GraduationCap, Headset, FileCog, Rocket, CheckCircle2 } from "lucide-react";
import { MaskedLines, Reveal, SectionHead } from "@/components/Motion";
import { API } from "@/lib/site";

const AUDIENCE = ["Independent garages", "Vehicle technicians", "Performance workshops", "Existing remapping companies", "New tuning businesses"];

const SUPPLY = [
  [Wrench, "Professional Tuning Tools", "Genuine Alientech, AutoTuner, Magic Motorsport and Dimsport hardware."],
  [Users, "Master / Slave Options", "Start as a Slave on our Master, or run your own Master with our file support."],
  [FileCog, "ECU Tuning Files", "Dyno-developed calibrations with fast turnaround through the File Portal."],
  [Headset, "Technical Support", "Direct access to experienced calibrators when a job gets complicated."],
  [Rocket, "Software & Tool Setup", "We configure your equipment, protocols and portal account with you."],
  [GraduationCap, "Training & Guidance", "Practical guidance on reading, writing and logging vehicles safely."],
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">{label}</span>
    {children}
  </label>
);
const inputCls = "w-full border border-line bg-surface px-4 py-3.5 text-sm text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors";

export default function Dealer() {
  const [form, setForm] = useState({ name: "", company: "", country: "", telephone: "", email: "", experience: "", tools: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/dealer-leads`, form);
      setDone(true);
      toast.success("Application received", { description: "Our dealer team will be in touch shortly." });
    } catch {
      toast.error("Something went wrong", { description: "Please try again or contact us directly." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="dealer-page">
      <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="container-regal relative">
          <MaskedLines
            lines={["Start Tuning", "With Regal"]}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight max-w-4xl"
          />
          <Reveal delay={0.5} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              Whether you are adding ECU tuning to an existing workshop or starting a new tuning business, we supply the equipment, software, tuning files and technical support to get you earning quickly.
            </p>
          </Reveal>
          <Reveal delay={0.62} className="mt-8 flex flex-wrap gap-2.5">
            {AUDIENCE.map((a) => (
              <span key={a} className="border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-300">{a}</span>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="border-b border-line" data-testid="dealer-dyno-section">
        <Reveal y={0}>
          <div className="relative overflow-hidden">
            <img src="/assets/workshop/dyno-audi-blue.webp" alt="Audi on the Regal Tuning dyno with REGAL TUNING screen" loading="lazy" className="aspect-[4/5] w-full object-cover object-[center_55%] sm:aspect-[21/9]" />
            <div className="absolute inset-0 bg-gradient-to-t from-void/50 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.24em] text-neutral-300 sm:left-12">The Regal Tuning workshop — dealer development &amp; training</p>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="dealer-supply-section">
        <div className="container-regal">
          <SectionHead overline="What we supply" title="Everything you need to start tuning" />
          <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
            {SUPPLY.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.05} className="bg-surface">
                <div className="h-full p-7">
                  <Icon size={22} className="text-regal-amber" strokeWidth={1.5} />
                  <p className="mt-4 font-display text-base font-bold uppercase tracking-wide">{t}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line py-20 sm:py-24" data-testid="dealer-form-section">
        <div className="container-regal grid gap-14 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHead
              overline="Apply"
              title="Become a Regal dealer"
              copy="Tell us about your business and what you want to achieve. A member of our dealer team will contact you to discuss tools, files and pricing."
            />
            <ul className="space-y-3">
              {["No franchise fees or lock-in contracts", "Dealer pricing on tools and files", "Ongoing file service with priority turnaround"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-neutral-300">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-regal-amber" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            {done ? (
              <div className="grid h-full min-h-[320px] place-items-center border border-regal-amber/40 bg-card p-10 text-center" data-testid="dealer-form-success">
                <div>
                  <CheckCircle2 size={48} className="mx-auto text-regal-amber" />
                  <p className="mt-5 font-display text-xl font-bold uppercase tracking-wide">Application received</p>
                  <p className="mt-3 max-w-sm text-sm text-neutral-400">Thank you — our dealer team will review your details and be in touch within one working day.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-line bg-card p-7 sm:p-10" data-testid="dealer-lead-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name *"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="dealer-name-input" /></Field>
                  <Field label="Company"><input value={form.company} onChange={set("company")} className={inputCls} data-testid="dealer-company-input" /></Field>
                  <Field label="Country *"><input required value={form.country} onChange={set("country")} className={inputCls} data-testid="dealer-country-input" /></Field>
                  <Field label="Telephone"><input type="tel" value={form.telephone} onChange={set("telephone")} className={inputCls} data-testid="dealer-telephone-input" /></Field>
                  <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="dealer-email-input" /></Field>
                  <Field label="Existing tuning experience">
                    <select value={form.experience} onChange={set("experience")} className={inputCls} data-testid="dealer-experience-select">
                      <option value="">Select…</option>
                      <option>None — new to tuning</option>
                      <option>Some — diagnostics / mechanical background</option>
                      <option>Experienced — already remapping</option>
                      <option>Established tuning business</option>
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Tools currently owned"><input value={form.tools} onChange={set("tools")} placeholder="e.g. KESS v2 Slave, Autotuner Master, none yet…" className={inputCls} data-testid="dealer-tools-input" /></Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Message"><textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} data-testid="dealer-message-input" /></Field>
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="dealer-lead-form-submit" className="mt-7 w-full bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Become a Dealer"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
